import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../api/client.js';
import type { ArtistAnalytics, RankedValue } from '../api/analyticsClient.js';
import { useAnalyticsClient } from '../hooks/useAnalyticsClient.js';
import { formatMetric, formatRate } from '../lib/metric.js';

/**
 * Cross-gig (per-artist) analytics (§26.1): most-requested songs with performed counts, the
 * request-to-performed rate, and the most-selected tempo categories, keys, meters and groove
 * families. Per §26.2 a dimension with no data arrives as `null` and is shown as "no data yet",
 * never as an empty zero.
 */

function RankedSection({ title, items }: { title: string; items: RankedValue[] | null }) {
  return (
    <section className="card">
      <h2>{title}</h2>
      {!items || items.length === 0 ? (
        <p className="muted">no data yet</p>
      ) : (
        <ul className="ranked-list">
          {items.map((item, i) => (
            <li key={`${item.value}-${i}`} className="ranked-row">
              <span className="ranked-row__rank">{i + 1}</span>
              <span className="ranked-row__label">{item.value}</span>
              <span className="ranked-row__count">{formatMetric(item.count)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function ArtistAnalyticsPage() {
  const { artistId = '' } = useParams();
  const analytics = useAnalyticsClient();
  const [data, setData] = useState<ArtistAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await analytics.getArtistAnalytics(artistId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load artist analytics.');
    }
  }, [analytics, artistId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <main className="page">
        <p className="error">{error}</p>
        <Link className="link" to="/app">
          Back to overview
        </Link>
      </main>
    );
  }
  if (!data) {
    return (
      <main className="page" aria-busy="true">
        <p>Loading artist analytics…</p>
      </main>
    );
  }

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to="/app">
              Overview
            </Link>{' '}
            / Artist analytics
          </p>
          <h1>Artist analytics</h1>
        </div>
      </header>

      <section className="card">
        <h2>Most requested songs</h2>
        <p className="help">
          Request-to-performed rate: <strong>{formatRate(data.requestToPerformedRate)}</strong>
        </p>
        {!data.mostRequestedSongs || data.mostRequestedSongs.length === 0 ? (
          <p className="muted">no data yet</p>
        ) : (
          <ul className="ranked-list">
            {data.mostRequestedSongs.map((song, i) => (
              <li key={song.songId} className="ranked-row">
                <span className="ranked-row__rank">{i + 1}</span>
                <span className="ranked-row__label">{song.songId}</span>
                <span className="ranked-row__count">
                  {formatMetric(song.requests)} req · {formatMetric(song.performed)} played
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <RankedSection title="Most selected tempo categories" items={data.mostSelectedTempoCategories} />
      <RankedSection title="Most selected keys" items={data.mostSelectedKeys} />
      <RankedSection title="Most selected meters" items={data.mostSelectedMeters} />
      <RankedSection title="Most selected groove families" items={data.mostSelectedGrooveFamilies} />
    </main>
  );
}
